import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import useEvent from "react-use-event-hook";

import {
  isNonProductionNavbarColorFromEnv,
  localStorageHelpers,
  Log,
  McpConnectorActionStatusEnum,
  OauthApi,
  RoutePaths,
  sessionStorageHelpers,
  verifyHttpOnlySessionActive,
  type IMcpConnectorActionReviewDto,
} from "@ryde/shared";

import { MCP_CONNECTOR_ACTION_ID_KEY } from "./mcp-connector-action.constants";
import type { TConnectorActionPageState } from "./mcp-connector-action.models";
import {
  canDecideConnectorAction,
  formatConnectorActionDetails,
  getConnectorActionStatusMessageKey,
  getMinutesLeft,
  isConnectorActionOwnedReturnTo,
  resolveConnectorActionPageState,
  runConnectorActionCall,
  translateConnectorActionError,
} from "./mcp-connector-action.utils";

/**
 * State machine behind the AI-connector action approval page (RYDE-8447): session probe → action fetch →
 * approve/deny → the outcome. The AI client opened the action and sent the user this page's link; the
 * client runs the action only after it is approved here. Without a session the page bounces through the
 * login page via `returnTo` and comes back (the action id is mirrored to sessionStorage).
 */
export const useMcpConnectorAction = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  // Same switch as the login page, so the shared title renders in the same colors.
  const isNonProductionLoginColors = useMemo(
    () => isNonProductionNavbarColorFromEnv(location.search || location.hash),
    [location.search, location.hash],
  );

  const urlActionId = searchParams.get("action_id");
  const actionId = useMemo(() => {
    if (urlActionId) {
      sessionStorageHelpers.setItem(MCP_CONNECTOR_ACTION_ID_KEY, urlActionId);
      return urlActionId;
    }
    return sessionStorageHelpers.getItem<string>(MCP_CONNECTOR_ACTION_ID_KEY);
  }, [urlActionId]);

  const [pageState, setPageState] =
    useState<TConnectorActionPageState>("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [decisionError, setDecisionError] = useState("");
  const [action, setAction] = useState<IMcpConnectorActionReviewDto | null>(
    null,
  );
  const [minutesLeft, setMinutesLeft] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // The action id of the page's current link. An answer that arrives for any other id is stale and dropped.
  const currentActionIdRef = useRef(actionId);

  const goToLogin = useEvent((): void => {
    localStorageHelpers.setItem(
      "returnTo",
      `${RoutePaths.MCP_CONNECTOR_ACTION}?action_id=${actionId}`,
    );
    navigate(RoutePaths.LOGIN);
  });

  // Back from login (or never needed it): nothing this page stored for the login round trip may outlive
  // it, or a later login would land here again, even when the user leaves without deciding.
  const endLoginRoundTrip = useEvent((): void => {
    sessionStorageHelpers.setItem(MCP_CONNECTOR_ACTION_ID_KEY, null);
    if (
      isConnectorActionOwnedReturnTo(
        localStorageHelpers.getItem<string>("returnTo"),
      )
    ) {
      localStorageHelpers.setItem("returnTo", null);
    }
  });

  const failWith = useEvent((message: string): void => {
    endLoginRoundTrip();
    setErrorMessage(message);
    setPageState("error");
  });

  const showAction = useEvent((review: IMcpConnectorActionReviewDto): void => {
    const nextState = resolveConnectorActionPageState(review.status);
    setAction(review);
    setMinutesLeft(
      getMinutesLeft({
        expiresAt: review.expiresAt,
        nowISO: new Date().toISOString(),
      }),
    );
    setPageState(nextState);
  });

  useEffect(() => {
    currentActionIdRef.current = actionId;
    const isCurrent = (): boolean => currentActionIdRef.current === actionId;
    // A new link in the same tab: nothing of the previous action may stay on screen, or be decided, while
    // this one loads.
    setAction(null);
    setPageState("loading");
    setErrorMessage("");
    setDecisionError("");
    setMinutesLeft(0);
    setIsSubmitting(false);

    const bootstrap = async (): Promise<void> => {
      if (!actionId) {
        failWith(t("mcpConnectorAction.requestInvalid"));
        return;
      }

      const isSessionActive = await verifyHttpOnlySessionActive();
      if (!isCurrent()) {
        return;
      }
      if (!isSessionActive) {
        goToLogin();
        return;
      }
      endLoginRoundTrip();

      const loaded = await runConnectorActionCall(() =>
        OauthApi.getConnectorAction(actionId),
      );
      if (!isCurrent()) {
        return;
      }
      if (!loaded.isSuccess) {
        failWith(translateConnectorActionError({ error: loaded.error, t }));
        return;
      }

      showAction(loaded.action);
    };

    bootstrap().catch(error => {
      Log.warn("AI connector action bootstrap failed", error);
      if (isCurrent()) {
        failWith(t("mcpConnectorAction.requestInvalid"));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionId]);

  const decide = useEvent(async (isApproved: boolean): Promise<void> => {
    if (
      !action ||
      !canDecideConnectorAction({ action, actionId, isSubmitting })
    ) {
      return;
    }
    // The action on screen, never just the link's: the two match here, and a stale answer is dropped below.
    const targetActionId = action.actionId;
    const isCurrent = (): boolean =>
      currentActionIdRef.current === targetActionId;
    setIsSubmitting(true);
    setDecisionError("");
    try {
      const decided = await runConnectorActionCall(() =>
        isApproved
          ? OauthApi.approveConnectorAction(targetActionId)
          : OauthApi.denyConnectorAction(targetActionId),
      );
      if (!isCurrent()) {
        return;
      }
      if (decided.isSuccess) {
        showAction({ ...action, ...decided.action });
        return;
      }

      // Decided in another tab, or expired meanwhile: the action's current state says what happened.
      const reloaded = await runConnectorActionCall(() =>
        OauthApi.getConnectorAction(targetActionId),
      );
      if (!isCurrent()) {
        return;
      }
      if (
        reloaded.isSuccess &&
        reloaded.action.status !== McpConnectorActionStatusEnum.Pending
      ) {
        showAction(reloaded.action);
        return;
      }

      setDecisionError(
        translateConnectorActionError({ error: decided.error, t }),
      );
    } finally {
      // A newer link reset the page itself; its own submit state is not this request's to clear.
      if (isCurrent()) {
        setIsSubmitting(false);
      }
    }
  });

  const handleApprove = useEvent(async (): Promise<void> => {
    await decide(true);
  });

  const handleDeny = useEvent(async (): Promise<void> => {
    await decide(false);
  });

  const statusMessageKey = action
    ? getConnectorActionStatusMessageKey(action.status)
    : undefined;

  return {
    pageState,
    errorMessage,
    decisionError,
    action,
    minutesLeft,
    statusMessageKey,
    details: action ? formatConnectorActionDetails(action) : "",
    isSubmitting,
    isNonProductionLoginColors,
    handleApprove,
    handleDeny,
  };
};
